/**
 * withProgress - Drop-in helper for async operations with Dynamic Progress
 * 
 * Usage:
 * const result = await withProgress(
 *   { label: 'Generating...', phases: ['Preparing', 'Working', 'Finalizing'] },
 *   async (signal) => api.doSomething({ payload, signal })
 * );
 */

import { useLoading } from '../contexts/LoadingContext';

/**
 * Wrap any async call with automatic progress tracking
 * @param {Object} opts - Progress options
 * @param {string} opts.label - Task label (e.g., "Generating script")
 * @param {string[]} opts.phases - Phase names for progress display
 * @param {boolean} opts.canCancel - Whether task can be cancelled (default: true)
 * @param {number} opts.timeoutMs - Timeout before showing extended warning (default: 20000)
 * @param {Object} opts.meta - Additional metadata for telemetry
 * @param {Function} runner - Async function to execute, receives AbortSignal
 * @returns {Promise} Result of runner function
 */
export async function withProgress(opts, runner) {
  // Use singleton loading manager
  const { startTask, completeTask, failTask, registerCancelCallback } = window.__loadingManager || {};
  
  if (!startTask) {
    console.warn('[withProgress] LoadingContext not initialized, falling back to direct execution');
    return runner(new AbortController().signal);
  }
  
  const task = startTask({
    label: opts.label,
    phases: opts.phases || ['Preparing', 'Working', 'Finalizing'],
    canCancel: opts.canCancel !== false,
    timeoutMs: opts.timeoutMs || 20000,
    meta: opts.meta || {}
  });

  const abortController = new AbortController();
  registerCancelCallback(task.id, () => abortController.abort());

  try {
    const result = await runner(abortController.signal);
    completeTask(task.id, { meta: opts.meta });
    return result;
  } catch (error) {
    // Don't log AbortError (user cancelled)
    if (error?.name !== 'AbortError') {
      failTask(task.id, { error: error?.message || 'Unknown error' });
    }
    throw error;
  }
}

/**
 * React hook version for component usage
 */
export function useProgressWrapper() {
  const loading = useLoading();
  
  return async (opts, runner) => {
    const task = loading.startTask({
      label: opts.label,
      phases: opts.phases || ['Preparing', 'Working', 'Finalizing'],
      canCancel: opts.canCancel !== false,
      timeoutMs: opts.timeoutMs || 20000,
      meta: opts.meta || {}
    });

    const abortController = new AbortController();
    loading.registerCancelCallback(task.id, () => abortController.abort());

    try {
      const result = await runner(abortController.signal);
      loading.completeTask(task.id, { meta: opts.meta });
      return result;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        loading.failTask(task.id, { error: error?.message || 'Unknown error' });
      }
      throw error;
    }
  };
}

/**
 * Network interceptor for automatic progress on slow requests
 * Add this to your API client setup
 */
export function createProgressInterceptor(fetch) {
  return async (url, options = {}) => {
    const shouldTrack = !options.skipProgress && options.method !== 'GET';
    
    if (!shouldTrack) {
      return fetch(url, options);
    }
    
    // Extract label from URL
    const urlPath = new URL(url, window.location.origin).pathname;
    const label = urlPath.split('/').filter(Boolean).pop() || 'Request';
    
    let taskId = null;
    const timeoutId = setTimeout(() => {
      // Only show progress if request takes > 600ms
      if (window.__loadingManager) {
        const task = window.__loadingManager.startTask({
          label: `Loading ${label}...`,
          phases: ['Sending request', 'Processing', 'Receiving data'],
          canCancel: false,
          timeoutMs: 30000,
          meta: { url: urlPath, method: options.method }
        });
        taskId = task.id;
      }
    }, 600);
    
    try {
      const response = await fetch(url, options);
      clearTimeout(timeoutId);
      
      if (taskId && window.__loadingManager) {
        window.__loadingManager.completeTask(taskId);
      }
      
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (taskId && window.__loadingManager) {
        window.__loadingManager.failTask(taskId, { error: error.message });
      }
      
      throw error;
    }
  };
}
